import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useToast } from '../../../common/toast/hooks/useToast'
import { AvailableModule, AvailableModuleFormProps } from './AvailableModule.d'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col, Form } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import slugify from 'react-slugify'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import RecordStatusField from '../../../common/forms/fields/RecordStatusField'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setAvailableModules } from '../../../../features/security/security.slice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faEdit,
    faHand,
} from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'
import SelectField from '../../../common/forms/fields/SelectField'
import ReadOnlyTextField from '../../../common/forms/fields/readonly/ReadOnlyTextField'
import { Choice } from '../../../common/forms/Forms.d'

const AvailableModuleForm: FC<AvailableModuleFormProps> = ({
    code = null,
    config = {},
    postFormSubmit,
}) => {
    const isViewOrEdit = code !== null
    const theme = useAppSelector((state) => state.theme.theme)

    const axiosPrivate = useAxiosPrivate()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const emptyAvailableModule: AvailableModule = {
        name: '',
        code: '',
        category_id: null,
        pages_id: null,
        has_main_page: false,
        has_records_template_page: false,
        active: false,
    }

    const AvailableModuleFormSchema = Yup.object().shape({
        name: Yup.string().trim().required('Enter a module name'),
        code: Yup.string().trim().required('Enter a unique code'),
        category_id: Yup.string().trim().required('Select a category'),
    })

    const [availableModuleDialog, setAvailableModuleDialog] = useState(false)
    const [availableModule, setAvailableModule] =
        useState<AvailableModule>(emptyAvailableModule)
    const [availableCategories, setAvailableCategories] = useState<Choice[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadAvailableModule = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/available-client-modules/view/' + code
            : '/available-client-modules/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })

            if (response.data?.success === true) {
                setAvailableCategories(response.data.data.available_categories)
                if (response.data.data?.module) {
                    setAvailableModule({
                        ...response.data.data.module,
                        has_main_page: response.data.data.module.pages_id
                            ? true
                            : false,
                        has_records_template_page: response.data.data.module
                            .records_template_page_id
                            ? true
                            : false,
                    })
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load module')
            }
        }
    }, [axiosPrivate, code, isViewOrEdit])

    useEffect(() => {
        if (availableModuleDialog) loadAvailableModule()
    }, [loadAvailableModule, availableModuleDialog])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        try {
            let response = null
            if (isViewOrEdit) {
                response = await axiosPrivate
                    .put('/available-client-modules/update/' + code, {
                        ...record,
                    })
                    .finally(() => {
                        setIsSubmitting(false)
                    })
            } else {
                response = await axiosPrivate
                    .post('/available-client-modules/create', { ...record })
                    .finally(() => {
                        setIsSubmitting(false)
                    })
            }

            if (response.data?.success === true) {
                dispatch(
                    setAvailableModules(response.data.data.available_modules)
                )
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
                navigate('/client/modules/' + record.code)
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }

            if (postFormSubmit) postFormSubmit()
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setAvailableModule(emptyAvailableModule)
        setAvailableModuleDialog(false)
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/available-client-modules/delete/' + code}
                    record={availableModule}
                    deletedCallback={(response) => {
                        hideDialog()
                        dispatch(
                            setAvailableModules(
                                response.data.data.available_modules
                            )
                        )
                        navigate('/')
                    }}
                    btnLabel="Delete"
                    showIcon={false}
                    confirmText={code}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete module:
                                <strong> {availableModule.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All data related to
                                this module will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'availableModule-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            <Common.Loader text="Loading..." visible={isLoading} />
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...availableModule,
                }}
                validationSchema={AvailableModuleFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm id="availableModule-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                {isViewOrEdit && (
                                    <Col sm={12}>
                                        <RecordStatusField
                                            id="formAvailableModuleActive"
                                            name="active"
                                            onLabel="De-Activate"
                                            offLabel="Activate"
                                            formikProps={formikProps}
                                        />
                                    </Col>
                                )}
                                <Col sm={12}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col md={4}>
                                                <TextField
                                                    id="formAvailableModuleName"
                                                    name="name"
                                                    label="Module name"
                                                    placeholder="Enter module name"
                                                    formikProps={formikProps}
                                                    onBlur={(e) => {
                                                        setAvailableModule(
                                                            (prevState) => {
                                                                return {
                                                                    ...prevState,
                                                                    name: e
                                                                        .target
                                                                        .value,
                                                                    code: slugify(
                                                                        formikProps
                                                                            .values
                                                                            .code ||
                                                                            formikProps
                                                                                .values
                                                                                .name
                                                                    ).replace(
                                                                        /-/g,
                                                                        '_'
                                                                    ),
                                                                }
                                                            }
                                                        )
                                                    }}
                                                    required
                                                />
                                            </Col>
                                            <Col md={4}>
                                                {isViewOrEdit ? (
                                                    <ReadOnlyTextField
                                                        id="formAvailableModuleCode"
                                                        name="code"
                                                        label="Code"
                                                        placeholder="Enter module code"
                                                        value={
                                                            formikProps.values
                                                                .code
                                                        }
                                                    />
                                                ) : (
                                                    <TextField
                                                        id="formAvailableModuleCode"
                                                        name="code"
                                                        label="Code"
                                                        placeholder="Enter module code"
                                                        formikProps={
                                                            formikProps
                                                        }
                                                        readonly={isViewOrEdit}
                                                        required
                                                    />
                                                )}
                                            </Col>
                                            <Col md={4}>
                                                {isViewOrEdit ? (
                                                    <ReadOnlyTextField
                                                        id="formAvailableCategories"
                                                        name="category_id"
                                                        label="Category"
                                                        value={
                                                            availableCategories.filter(
                                                                (c) =>
                                                                    c.id ===
                                                                    formikProps
                                                                        .values
                                                                        .category_id
                                                            )[0]?.name || ''
                                                        }
                                                    />
                                                ) : (
                                                    <SelectField
                                                        id="formAvailableCategories"
                                                        name="category_id"
                                                        label="Category"
                                                        formikProps={
                                                            formikProps
                                                        }
                                                        choices={
                                                            availableCategories
                                                        }
                                                        required
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Pages</legend>
                                        <Row>
                                            <Col sm={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formPagesIdFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Include Main Page"
                                                        name="has_main_page"
                                                        checked={
                                                            formikProps.values
                                                                .has_main_page
                                                        }
                                                        onChange={(e) => {
                                                            formikProps.setFieldValue(
                                                                'has_main_page',
                                                                e.target.checked
                                                            )
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col sm={6}>
                                                <Form.Group
                                                    controlId={
                                                        'formRecordsTemplatePageIdFlag'
                                                    }
                                                >
                                                    <Form.Switch
                                                        type="checkbox"
                                                        label="Include Records Template Page"
                                                        name="has_records_template_page"
                                                        checked={
                                                            formikProps.values
                                                                .has_records_template_page
                                                        }
                                                        onChange={(e) => {
                                                            formikProps.setFieldValue(
                                                                'has_records_template_page',
                                                                e.target.checked
                                                            )
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                {availableModule.pages_id &&
                                                    !formikProps.values
                                                        .has_main_page && (
                                                        <Alert
                                                            variant="danger"
                                                            className="danger mt-3 mb-1"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faHand}
                                                                className="me-2"
                                                            />{' '}
                                                            <strong>
                                                                Wait!
                                                            </strong>{' '}
                                                            The main page and
                                                            all its sections
                                                            will be{' '}
                                                            <strong>
                                                                lost permemnatly
                                                            </strong>
                                                            .
                                                        </Alert>
                                                    )}
                                            </Col>
                                        </Row>
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="availableModule-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {isViewOrEdit ? (
                <Button
                    variant="warning"
                    size="sm"
                    onClick={() => setAvailableModuleDialog(true)}
                >
                    <FontAwesomeIcon icon={faEdit} />

                    <span className="d-none d-sm-inline-block ms-2">
                        Edit Module
                    </span>
                </Button>
            ) : (
                <Link
                    to={'#'}
                    className={`collapse-item ${
                        config.triggerBtnVariant
                            ? `btn btn-${config.triggerBtnVariant}`
                            : ''
                    } ${
                        config.triggerBtnClassName
                            ? config.triggerBtnClassName
                            : ''
                    }`}
                    onClick={() => setAvailableModuleDialog(true)}
                >
                    {config?.triggerBtnLabel || 'Create new module'}
                </Link>
            )}
            <Dialog
                visible={availableModuleDialog}
                breakpoints={{
                    '1240px': '70vw',
                    '960px': '90vw',
                    '640px': '90vw',
                }}
                style={{ width: '50vw' }}
                header={
                    isViewOrEdit
                        ? isLoading
                            ? 'Loading...'
                            : availableModule.name ||
                              config?.triggerBtnLabel ||
                              'Edit module'
                        : config?.triggerBtnLabel || 'Create new module'
                }
                modal
                className="p-fluid"
                footer={formFooter}
                onHide={hideDialog}
                maximizable
                contentClassName={isLoading ? 'wrapped-content' : ''}
                closeOnEscape={false}
            >
                {FormContent}
            </Dialog>
        </>
    )
}

export default AvailableModuleForm
